/* eslint-disable arrow-body-style */
import { DateTime } from 'luxon';

/**
 * Returns a localized string representing this date.
 * Defaults to the "en-us" locale if no locale has been specified.
 *
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00') //=> Nov 29, 2023
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00', 'es') //=> 29 nov 2023
 */
const formatDateMedium = (date: string | null, locale?: string | null) => {
  return date
    ? DateTime.fromISO(date, { setZone: true }).setLocale(locale ?? 'en-us').toLocaleString(DateTime.DATE_MED)
    : '';
};

/**
 * Returns a localized string representing this date.
 * Defaults to the "en-us" locale if no locale has been specified.
 *
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00') //=> 11/29/2023
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00', 'es') //=> 29/11/2023
 */
const formatDateShort = (date: string | null, locale?: string | null) => {
  return date
    ? DateTime.fromISO(date, { setZone: true }).setLocale(locale ?? 'en-us').toLocaleString(DateTime.DATE_SHORT)
    : '';
};

/**
 * Returns a string representation of this DateTime formatted as a four-digit year.
 *
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00') //=> 2023
 */
const formatDateYear = (date: string | null) => {
  return date
    ? Number(DateTime.fromISO(date, { setZone: true }).toFormat('yyyy'))
    : 0;
};

/**
 * Returns a localized string representing the time with user's current timezone.
 * Defaults to the "en-us" locale if no locale has been specified.
 *
 * @example
 * formatDateTimeLocal('2022-10-01T10:00-04:00') //=> 9:00 AM CST
 * @example
* formatDateTimeLocal('2022-12-01T10:00-05:00', 'it') //=> 16:00 GMT+1
 */
const formatTimeLocal = (date: string | null, locale?: string | null) => {
  return date
    ? `${DateTime.fromISO(date).setZone().setLocale(locale ?? 'en-us').toLocaleString(DateTime.TIME_SIMPLE)}`
    : '';
};

/**
 * Returns the epoch milliseconds of this DateTime.
 *
 * @example
 * formatDateMedium('2023-11-29T08:12:33-07:00') //=> 1701270753000
 */
const getTimeEpoch = (date: string | null) => {
  return date
    ? DateTime.fromISO(date, { setZone: true }).toMillis()
    : 0;
};

export {
  formatDateMedium,
  formatDateShort,
  formatDateYear,
  formatTimeLocal,
  getTimeEpoch,
};
